import { createSlice } from "@reduxjs/toolkit";
import { LiveUser, Meta } from "../types";
import {
  doFetchLiveUserList,
  doFetchViewersList,
  doGenerateAgoraToken,
} from "./live.action";

interface liveUserState {
  liveUsers: LiveUser[];
  meta: Meta | null;
  loading: boolean;
  error: string | null;
  searchQuery: string;
  agoraTokenData: {
    rtcToken?: string;
    rtmToken?: string;
    channelName?: string;
    uid?: number;
  } | null;
  isLive: boolean;
  isConnecting: boolean;
  liveError: string | null;
  audienceCount: number;
  liveSessionId: string | null;
  viewers: any[];
  viewersMeta: Meta | null;
  viewersLoading: boolean;
}

const initialState: liveUserState = {
  liveUsers: [] as LiveUser[],
  meta: null,
  loading: false,
  error: null,
  searchQuery: "",
  agoraTokenData: null,
  isLive: false,
  isConnecting: false,
  liveError: null,
  audienceCount: 0,
  liveSessionId: null,
  viewers: [],
  viewersMeta: null,
  viewersLoading: false,
};

export const liveUserSlice = createSlice({
  name: "liveUser",
  initialState,
  reducers: {
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    clearSearch: (state) => {
      state.searchQuery = "";
    },
    setConnecting: (state, action) => {
      state.isConnecting = action.payload;
    },
    setLive: (state, action) => {
      state.isLive = action.payload;
      if (!action.payload) {
        // Reset live state when ending broadcast
        state.audienceCount = 0;
        state.liveSessionId = null;
        state.liveError = null;
      }
    },
    setLiveError: (state, action) => {
      state.liveError = action.payload;
    },
    setAudienceCount: (state, action) => {
      state.audienceCount = action.payload;
    },
    setLiveSessionId: (state, action) => {
      state.liveSessionId = action.payload;
    },
    resetLiveState: (state) => {
      state.isLive = false;
      state.isConnecting = false;
      state.liveError = null;
      state.audienceCount = 0;
      state.liveSessionId = null;
    },
  },
  extraReducers: (builder) => {
    // fetch liveusers list
    builder
      .addCase(doFetchLiveUserList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doFetchLiveUserList.fulfilled, (state, action) => {
        state.loading = false;
        state.liveUsers = action.payload.data;
        state.meta = action.payload.meta;
      })
      .addCase(doFetchLiveUserList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // generate agora token
    builder
      .addCase(doGenerateAgoraToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(doGenerateAgoraToken.fulfilled, (state, action) => {
        state.loading = false;
        state.agoraTokenData = action.payload;
      })
      .addCase(doGenerateAgoraToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Fetch viewers list
    builder
      .addCase(doFetchViewersList.pending, (state) => {
        state.viewersLoading = true;
        state.error = null;
      })
      .addCase(doFetchViewersList.fulfilled, (state, action) => {
        state.viewersLoading = false;
        state.viewers = action.payload.data;
        state.viewersMeta = action.payload.meta;
      })
      .addCase(doFetchViewersList.rejected, (state, action) => {
        state.viewersLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSearchQuery,
  clearSearch,
  setConnecting,
  setLive,
  setLiveError,
  setAudienceCount,
  setLiveSessionId,
  resetLiveState,
} = liveUserSlice.actions;

export default liveUserSlice.reducer;
